import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAppSelector } from 'slices/commonSelectors';
import { selectCombinedUser } from 'slices/commonSelectors';
import { WorkspaceType } from 'types/workspace';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { Channel } from 'types/channels';
import { keys as workspaceKeys } from './useFetchExerciseWorkspaceTeamStatus';
import { keys as incidentKeys } from './useFetchIncidentWorkspaceTeamStatus';

type GenerateCustomTeamSiteInput = {
  workspaceType: WorkspaceType;
  workspaceId: UUID;
  channels: Channel[];
  tenantId: UUID;
  description?: string;
  label?: string;
};

const invalidateWorkspaceStatusQuery = (
  workspaceType: WorkspaceType,
  workspaceId: UUID,
  queryClient: QueryClient
) => {
  if (workspaceType === 'exercise') {
    queryClient.invalidateQueries({
      queryKey: [workspaceKeys.exerciseWorkspaceTeamStatus, workspaceId],
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: [incidentKeys.incidentWorkspaceTeamStatus, workspaceId],
    });
  }
};

export const useGenerateCustomTeamSite = () => {
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCombinedUser);
  const userEmail = user.email_address;
  const userId = user.user_guid;

  return useMutation<any, any, GenerateCustomTeamSiteInput>({
    mutationFn: async ({
      workspaceType,
      workspaceId,
      tenantId,
      channels,
    }: GenerateCustomTeamSiteInput) => {
      toast.info('Launching your custom Teams site');
      const response = await axios(config.apiGateway.customTeamSites, {
        method: 'POST',
        data: {
          workspaceType,
          workspaceId,
          tenantId,
          channels,
          userEmail,
          userId,
        },
      });
      return response?.data;
    },
    onSuccess: ({ message, status }, { workspaceType, workspaceId }) => {
      if (status === 'In progress') {
        toast.success(message);
      }

      invalidateWorkspaceStatusQuery(workspaceType, workspaceId, queryClient);
    },
    onError: (error) => {
      toast.error(error.response.data.error);
    },
  });
};
